import React, { useMemo, useRef, useState } from "react";
import { Download, FileDown, Upload, CheckCircle2, Wrench, Workflow, Rocket, FlaskConical, ShieldAlert, Info, ChevronDown } from "lucide-react";

// Sample JSON (used as initial state; user can paste/replace)
const sampleData = {
  "execution_layer": {
    "scenario_summary": "A fully automated anime video production pipeline using ChatGPT (GPT-4), Storyboarder, Stable Diffusion/Leonardo.ai, EbSynth + Deforum, PlayHT/ElevenLabs, and Adobe Premiere Pro/DaVinci Resolve.",
    "prerequisites": [
      "OpenAI account with access to GPT-4 or GPT-4o (for scripting)",
      "Storyboarder (free, downloaded from Wonder Unit) or alternative storyboarding tool",
      "Leonardo.ai account or Stable Diffusion installed locally (AUTOMATIC1111 or similar)",
      "EbSynth (free) and Deforum SD (Google Colab or local Python setup)",
      "PlayHT, ElevenLabs, or Coqui TTS accounts for AI voice generation",
      "Adobe Premiere Pro or DaVinci Resolve installed (free version available for Resolve)",
      "Project directory (for organizing scripts, images, videos, audio)",
      "Initial anime concept or written prompt, genre/theme preferences"
    ],
    "step_by_step_guide": [
      {
        "step_number": 1,
        "title": "Script & Storyboard Generation",
        "description": "Use ChatGPT (GPT-4 or GPT-4o) to generate a detailed anime script and scene-by-scene breakdown. Then, create storyboards for each scene using Storyboarder, sketching or importing reference images.",
        "tool": "ChatGPT (Advanced / GPT-4 or GPT-4o), Storyboarder by Wonder Unit",
        "prompt_examples": [
          "Write a complete 10-minute anime script in the fantasy adventure genre. Include character names, setting descriptions, and scene-by-scene breakdowns.",
          "For each scene, give a one-line visual description suitable for a storyboard panel."
        ],
        "api_or_no_code_setup": "Use OpenAI's web app or Playground. For API: `openai.ChatCompletion.create(model='gpt-4', messages=[...])`. Output can be copied to a text/Word document. Download and install Storyboarder. Open the app, and for each scene, either sketch the panel or import AI-generated reference images as needed. Save the storyboard as an image, PDF, or original .storyboarder file.",
        "expected_output": "Anime script document (.docx/.txt) and storyboard panels (images or PDF) for each key scene."
      },
      {
        "step_number": 2,
        "title": "Character & Background Design",
        "description": "Use the final storyboard and script to generate anime-style character and background images in Leonardo.ai (online) or Stable Diffusion (local). For each character/scene, write prompts specifying style, emotion, pose, and context.",
        "tool": "Stable Diffusion / Leonardo.ai",
        "prompt_examples": [
          "An anime-style teenage boy with spiky blue hair, wearing a futuristic school uniform, standing confidently, colorful background.",
          "Anime background: mystical forest at dusk, glowing lights, gentle mist, Ghibli-inspired."
        ],
        "api_or_no_code_setup": "On Leonardo.ai: Log in, enter your prompt, select anime models, and download outputs. On Stable Diffusion: Run the prompt in your preferred GUI (like AUTOMATIC1111 UI) or via Python API. Save and name images by character/scene.",
        "expected_output": "A neatly organized collection of character and background images, ready for animation."
      },
      {
        "step_number": 3,
        "title": "Animation Generation",
        "description": "Combine character and background images to make animated scenes. Use EbSynth for 'keyframe-to-motion' animation (draw a start and end keyframe), or Deforum SD (for text-to-animation or image-to-animation) to generate short animated clips per storyboard cues.",
        "tool": "EbSynth + Deforum Stable Diffusion",
        "prompt_examples": [
          "Animate this character walking from left to right across the scene using these two keyframe poses and background.",
          "Deforum config: Animate scene 3 with the protagonist running towards the camera for 48 frames, anime style."
        ],
        "api_or_no_code_setup": "EbSynth: Place keyframe sketches and reference video in the EbSynth project folder. Use the GUI to generate in-between animated frames. For Deforum: Run the official Google Colab notebook or local setup, input images, adjust animation settings, run and download mp4 outputs.",
        "expected_output": "Short animated video clips (MP4) corresponding to each storyboarded scene."
      },
      {
        "step_number": 4,
        "title": "Voice Synthesis & Sound Design",
        "description": "Use PlayHT, ElevenLabs, or Coqui to convert your script into anime-style AI voice-acted dialogue. Also generate sound effects and background music using these tools or royalty-free SFX/music libraries if needed.",
        "tool": "PlayHT, ElevenLabs, or Coqui",
        "prompt_examples": [
          "Japanese high-school girl, cheerful voice: 'Let's do our best today!'",
          "Serious older mentor, calm tone: 'Remember, courage is not the absence of fear.'"
        ],
        "api_or_no_code_setup": "Sign in to the TTS provider. Paste lines in web dashboard or use API (e.g., ElevenLabs Python: `requests.post('https://api.elevenlabs.io/v1/text-to-speech/[voice_id]')`). Download all audio files and organize by scene/character.",
        "expected_output": "A set of cleanly labeled audio files for dialogue, SFX, and music, ready to be synced with animation."
      },
      {
        "step_number": 5,
        "title": "Editing & Syncing",
        "description": "In Adobe Premiere Pro or DaVinci Resolve, import the animated clips, voiceovers, music, and SFX. Sync all audio to the animation per the storyboard, add visual transitions and effects, and export the final anime video.",
        "tool": "Adobe Premiere Pro / DaVinci Resolve",
        "prompt_examples": [
          "Place Scene 1 video clip; sync protagonist's voice lines (audio 1–3); fade in music and add whoosh SFX on action.",
          "Apply glow and vignette effect to the transformation sequence before rendering."
        ],
        "api_or_no_code_setup": "Import all assets into the project. Place video/audio onto the timeline, adjust timing as per storyboard, add transitions/effects. Use built-in tools to render/export the final video (recommend exporting as .mp4 for sharing or .mov for archival).",
        "expected_output": "A professionally edited, fully synced anime video file (MP4 or MOV), ready to publish or distribute."
      }
    ],
    "tool_starter_kits": [
      {
        "tool_name": "ChatGPT (Advanced / GPT-4 or GPT-4o)",
        "getting_started": [
          "Sign up or log in to OpenAI and ensure GPT-4 access is enabled.",
          "Access the web app or API.",
          "Learn basic prompt engineering (refer to OpenAI prompt guides)."
        ],
        "sample_prompt_templates": [
          "Write a 5-scene anime episode featuring a magical high school club, with character introductions and tension.",
          "For scene 2: describe the environment and character actions as visually as possible for artists."
        ],
        "importable_assets": [
          "Sample anime script DOCX",
          "Scene breakdown spreadsheet CSV"
        ]
      },
      {
        "tool_name": "Storyboarder by Wonder Unit",
        "getting_started": [
          "Download and install Storyboarder from Wonder Unit.",
          "Create a new project and add panels per scene.",
          "Use the sketch tool, type scene descriptions, or import AI images."
        ],
        "sample_prompt_templates": [
          "Describe this moment so it can be drawn clearly: [paste script excerpt]",
          "Paste or upload a generated character image to visualize this beat."
        ],
        "importable_assets": [
          "Sample .storyboarder file",
          "Blank storyboard template PDF"
        ]
      },
      {
        "tool_name": "Stable Diffusion / Leonardo.ai",
        "getting_started": [
          "Sign up for Leonardo.ai or download a Stable Diffusion release and suitable anime checkpoint(s).",
          "Practice simple and complex prompts for anime character/background images.",
          "Familiarize yourself with batch image export and file organization."
        ],
        "sample_prompt_templates": [
          "Anime girl with sea-green hair, holding a sword, dramatic lighting, Makoto Shinkai style.",
          "Spacious anime classroom, warm morning sunlight, empty chairs."
        ],
        "importable_assets": [
          "Prompt formula cheatsheet PDF",
          "Anime model checkpoint .ckpt file"
        ]
      },
      {
        "tool_name": "EbSynth + Deforum Stable Diffusion",
        "getting_started": [
          "Download and install EbSynth (ebsynth.com), prepare keyframes and reference video if needed.",
          "Access Deforum Colab Notebook or install Deforum add-on for Stable Diffusion.",
          "Organize your scenes into folders and prep input images."
        ],
        "sample_prompt_templates": [
          "Interpolate the movement between these two anime character poses over 2 seconds.",
          "Deforum: Animate character floating upwards for 32 frames using the following settings..."
        ],
        "importable_assets": [
          "Sample keyframe PNGs",
          "Deforum Colab template link"
        ]
      },
      {
        "tool_name": "PlayHT (AI Voice), ElevenLabs, or Coqui",
        "getting_started": [
          "Sign up, select anime-style or character voice models.",
          "Familiarize yourself with the batch script and file download systems.",
          "Check platform quotas and plan for dialogue/SFX volume."
        ],
        "sample_prompt_templates": [
          "Generate a bubbly anime girl's voice for: '[dialogue]'",
          "Elderly sensei character, composed and wise: '[dialogue]'"
        ],
        "importable_assets": [
          "Sample script TXT file",
          "Pre-generated voice clip WAV"
        ]
      },
      {
        "tool_name": "Adobe Premiere Pro / DaVinci Resolve",
        "getting_started": [
          "Install the video editor and create a new project.",
          "Set up bins for animation, voices, music, and SFX.",
          "Import all assets, arrange them on a timeline, and study core editing/sync tools."
        ],
        "sample_prompt_templates": [
          "Sync this dialogue with the corresponding video lip flaps/frame timing.",
          "Add transition and color grading to create an anime-style vignette."
        ],
        "importable_assets": [
          "Sample timeline project file",
          "Export settings preset JSON"
        ]
      }
    ],
    "automation_recipes": [
      {
        "platform": "Zapier / Make / Custom API",
        "trigger": "Script approved and saved to project folder.",
        "actions": [
          "Trigger Stable Diffusion or Leonardo.ai to generate image batches from script/scene prompts.",
          "Auto-upload finished images to shared folder for animator/next workflow step.",
          "Alert animation lead when new assets are available."
        ],
        "expected_result": "Script automatically drives visual asset creation and moves assets efficiently to the animation phase."
      },
      {
        "platform": "Zapier / Make / API",
        "trigger": "New audio/voiceover file uploaded to the 'audio' folder.",
        "actions": [
          "Notify editor via email or app.",
          "Automatically insert the new audio file onto the correct track in the video editor project (if supported by integration/API)."
        ],
        "expected_result": "Voiceovers are instantly ready for syncing, speeding up editing."
      }
    ],
    "testing_and_optimization": {
      "test_plan": [
        "Check that script, storyboard, image assets, animation, and audio all align with scene breakdowns.",
        "Review rendered scenes for visual cohesiveness and timing.",
        "Test all AI tools with sample data to confirm expected style and quality.",
        "Perform a dry run of the editing workflow with placeholder assets."
      ],
      "optimization_loop": [
        "Gather feedback from viewers or team after first draft.",
        "Refine prompts for specific issues: re-run art/animation/audio outputs as needed.",
        "Adjust storyboard timing, dialogue pacing, or color grading based on test renders.",
        "Automate batch re-processing for multiple takes/variations."
      ],
      "fallback_options": [
        "Switch to NovelAI for scripting if ChatGPT/GPT-4 is unavailable.",
        "Use Boords for rapid storyboarding if Storyboarder has issues.",
        "Switch to Midjourney for image generation if SD/Leonardo are down (with manual downloads).",
        "Try Kaiber AI for animation if EbSynth/Deforum stalls.",
        "Use Amazon Polly (basic voices) if PlayHT/ElevenLabs/Coqui are down.",
        "Use CapCut for basic editing if Premiere/Resolve is not available.",
        "Perform manual syncing and editing if automation systems fail."
      ]
    }
  }
};

// UI Components built with Tailwind
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = "" }) => (
  <div className={`p-6 pb-2 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold leading-none tracking-tight ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-6 pt-2 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "default", className = "", ...props }) => {
  const baseClasses = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background";
  const variants = {
    default: "bg-slate-900 text-white hover:bg-slate-900/90 h-10 py-2 px-4",
    outline: "border border-gray-200 hover:bg-gray-100 hover:text-gray-900 h-10 py-2 px-4",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-100/80 h-10 py-2 px-4"
  };
  
  return (
    <button 
      onClick={onClick} 
      className={`${baseClasses} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Tabs = ({ children, defaultValue, className = "" }) => {
  const [activeTab, setActiveTab] = useState(defaultValue);
  
  return (
    <div className={`${className}`} data-active-tab={activeTab} onChange={setActiveTab}>
      {React.Children.map(children, child =>
        React.cloneElement(child, { activeTab, setActiveTab })
      )}
    </div>
  );
};

const TabsList = ({ children, className = "", activeTab, setActiveTab }) => (
  <div className={`inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1 text-gray-500 ${className}`}>
    {React.Children.map(children, child =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({ children, value, activeTab, setActiveTab, className = "" }) => (
  <button
    onClick={() => setActiveTab(value)}
    className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
      activeTab === value 
        ? "bg-white text-gray-950 shadow-sm" 
        : "hover:bg-gray-200 hover:text-gray-900"
    } ${className}`}
  >
    {children}
  </button>
);

const TabsContent = ({ children, value, activeTab, className = "" }) => (
  activeTab === value ? (
    <div className={`mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}>
      {children}
    </div>
  ) : null
);

const Accordion = ({ children, type, collapsible, className = "" }) => {
  return <div className={`${className}`}>{children}</div>;
};

const AccordionItem = ({ children, value, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className={`border-b ${className}`}>
      {React.Children.map(children, child =>
        React.cloneElement(child, { isOpen, setIsOpen, value })
      )}
    </div>
  );
};

const AccordionTrigger = ({ children, isOpen, setIsOpen, className = "" }) => (
  <button
    onClick={() => setIsOpen(!isOpen)}
    className={`flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline ${className}`}
  >
    {children}
    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
  </button>
);

const AccordionContent = ({ children, isOpen, className = "" }) => (
  isOpen ? (
    <div className={`pb-4 pt-0 ${className}`}>
      {children}
    </div>
  ) : null
);

const Badge = ({ children, variant = "default", className = "" }) => {
  const variants = {
    default: "bg-slate-900 text-white",
    secondary: "bg-gray-100 text-gray-900",
    outline: "border border-gray-200 text-gray-900"
  };
  
  return (
    <div className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
};

const Input = ({ className = "", ...props }) => (
  <input
    className={`flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Textarea = ({ className = "", ...props }) => (
  <textarea
    className={`flex min-h-[80px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Separator = ({ className = "" }) => (
  <div className={`shrink-0 bg-gray-200 h-[1px] w-full ${className}`} />
);

// Small UI helpers
const SectionTitle = ({ icon: Icon, title, hint }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 opacity-80" />
      <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
    </div>
    {hint ? (
      <span className="text-sm text-gray-500">{hint}</span>
    ) : null}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
    <span className="text-2xl font-bold">{value}</span>
  </div>
);

// Main Component
export default function AIExecutionDashboard() {
  const [jsonText, setJsonText] = useState(JSON.stringify(sampleData, null, 2));
  const [data, setData] = useState(sampleData);
  const [error, setError] = useState("");
  const printRef = useRef(null);

  const ex = data?.execution_layer || {};

  const meta = useMemo(() => {
    return {
      prerequisites: ex.prerequisites?.length || 0,
      steps: ex.step_by_step_guide?.length || 0,
      toolkits: ex.tool_starter_kits?.length || 0,
      automations: ex.automation_recipes?.length || 0,
      tests: ex.testing_and_optimization?.test_plan?.length || 0,
      optimizations: ex.testing_and_optimization?.optimization_loop?.length || 0,
      fallbacks: ex.testing_and_optimization?.fallback_options?.length || 0,
    };
  }, [data]);

  const loadJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setData(parsed);
      setError("");
    } catch (e) {
      setError("Invalid JSON. Please fix the syntax and try again.");
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => setJsonText(String(evt.target?.result || ""));
    reader.readAsText(file);
  };

  const downloadPDF = () => {
    // Placeholder for PDF download functionality
    alert("PDF download would be implemented here - could use jsPDF or similar library");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900">
      {/* Top Bar */}
      <div className="sticky top-0 z-20 backdrop-blur bg-white/70 border-b">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center shadow">
              <Workflow className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-tight">AI Execution Layer Report</h1>
              <p className="text-xs text-gray-500">Elegant, clear, and printable dashboard</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={downloadPDF} variant="outline" className="gap-2">
              <FileDown className="h-4 w-4" /> Download PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Panel: Controls */}
        <Card className="lg:col-span-1 order-2 lg:order-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Upload className="h-4 w-4" /> Load/Replace JSON
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input type="file" accept="application/json,.json" onChange={handleFile} />
            <Textarea value={jsonText} onChange={(e) => setJsonText(e.target.value)} className="font-mono text-xs min-h-[160px]" />
            {error && <div className="text-sm text-red-600">{error}</div>}
            <Button onClick={loadJson} className="w-full">Apply JSON</Button>
            <p className="text-xs text-gray-500">
              Paste your API response here or upload a .json file, then click <strong>Apply JSON</strong>.
            </p>
          </CardContent>
        </Card>

        {/* Right Panel: Dashboard */}
        <div className="lg:col-span-2 order-1 lg:order-2 space-y-4">
          {/* Summary */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <SectionTitle icon={Info} title="Scenario Summary" />
              <p className="text-sm leading-relaxed text-slate-700">
                {ex.scenario_summary || "—"}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                <div className="rounded-2xl border p-4 bg-white">
                  <Stat label="Prerequisites" value={meta.prerequisites} />
                </div>
                <div className="rounded-2xl border p-4 bg-white">
                  <Stat label="Steps" value={meta.steps} />
                </div>
                <div className="rounded-2xl border p-4 bg-white">
                  <Stat label="Toolkits" value={meta.toolkits} />
                </div>
                <div className="rounded-2xl border p-4 bg-white">
                  <Stat label="Automations" value={meta.automations} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs defaultValue="steps" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="steps">Steps</TabsTrigger>
              <TabsTrigger value="tools">Toolkits</TabsTrigger>
              <TabsTrigger value="automation">Automation</TabsTrigger>
              <TabsTrigger value="qa">Testing & Ops</TabsTrigger>
            </TabsList>

            {/* Steps */}
            <TabsContent value="steps">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><Rocket className="h-4 w-4"/> Step-by-step Guide</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {(ex.step_by_step_guide || []).map((s, idx) => (
                      <div key={idx} className="p-6 hover:bg-slate-50 transition">
                        <div className="flex items-start gap-3">
                          <Badge className="rounded-full">Step {s.step_number ?? idx + 1}</Badge>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-lg">{s.title}</h4>
                              <Badge variant="outline">{s.tool}</Badge>
                            </div>
                            <p className="text-sm mt-2 text-slate-700">{s.description}</p>

                            <Accordion type="single" collapsible className="mt-3">
                              <AccordionItem value={`p-${idx}`}>
                                <AccordionTrigger className="text-sm">Prompt Examples</AccordionTrigger>
                                <AccordionContent>
                                  <ul className="list-disc pl-5 text-sm space-y-1">
                                    {(s.prompt_examples || []).map((p, i) => (
                                      <li key={i}>{p}</li>
                                    ))}
                                  </ul>
                                </AccordionContent>
                              </AccordionItem>
                              <AccordionItem value={`setup-${idx}`}>
                                <AccordionTrigger className="text-sm">Setup / API</AccordionTrigger>
                                <AccordionContent>
                                  <p className="text-sm whitespace-pre-line">{s.api_or_no_code_setup}</p>
                                </AccordionContent>
                              </AccordionItem>
                              <AccordionItem value={`output-${idx}`}>
                                <AccordionTrigger className="text-sm">Expected Output</AccordionTrigger>
                                <AccordionContent>
                                  <p className="text-sm">{s.expected_output}</p>
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Toolkits */}
            <TabsContent value="tools">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><Wrench className="h-4 w-4"/> Tool Starter Kits</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {(ex.tool_starter_kits || []).map((t, idx) => (
                      <div key={idx} className="rounded-2xl border p-4 bg-white">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold">{t.tool_name}</h4>
                          <Badge variant="secondary">Kit</Badge>
                        </div>
                        <Separator className="my-3" />
                        <div className="space-y-3">
                          <div>
                            <h5 className="text-sm font-medium">Getting Started</h5>
                            <ul className="list-disc pl-5 text-sm space-y-1">
                              {(t.getting_started || []).map((step, i) => (
                                <li key={i}>{step}</li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h5 className="text-sm font-medium">Sample Prompts</h5>
                            <ul className="list-disc pl-5 text-sm space-y-1">
                              {(t.sample_prompt_templates || []).map((prompt, i) => (
                                <li key={i} className="text-slate-600 italic">{prompt}</li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h5 className="text-sm font-medium">Importable Assets</h5>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(t.importable_assets || []).map((asset, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{asset}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Automation */}
            <TabsContent value="automation">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><Workflow className="h-4 w-4"/> Automation Recipes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(ex.automation_recipes || []).map((recipe, idx) => (
                      <div key={idx} className="rounded-2xl border p-4 bg-white">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold">{recipe.platform}</h4>
                          <Badge variant="secondary">Recipe</Badge>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <h5 className="text-sm font-medium flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              Trigger
                            </h5>
                            <p className="text-sm text-slate-600 ml-6">{recipe.trigger}</p>
                          </div>
                          
                          <div>
                            <h5 className="text-sm font-medium">Actions</h5>
                            <ul className="list-disc pl-5 text-sm space-y-1">
                              {(recipe.actions || []).map((action, i) => (
                                <li key={i}>{action}</li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h5 className="text-sm font-medium">Expected Result</h5>
                            <p className="text-sm text-slate-600">{recipe.expected_result}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Testing & Ops */}
            <TabsContent value="qa">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><FlaskConical className="h-4 w-4"/> Testing & Optimization</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Test Plan */}
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-3">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        Test Plan
                      </h4>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        {(ex.testing_and_optimization?.test_plan || []).map((test, i) => (
                          <li key={i}>{test}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Optimization Loop */}
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-3">
                        <Rocket className="h-4 w-4 text-green-600" />
                        Optimization Loop
                      </h4>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        {(ex.testing_and_optimization?.optimization_loop || []).map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Fallback Options */}
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-3">
                        <ShieldAlert className="h-4 w-4 text-orange-600" />
                        Fallback Options
                      </h4>
                      <div className="grid md:grid-cols-2 gap-2">
                        {(ex.testing_and_optimization?.fallback_options || []).map((fallback, i) => (
                          <div key={i} className="text-sm p-3 rounded-lg bg-orange-50 border border-orange-200">
                            {fallback}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Prerequisites Section */}
          {ex.prerequisites && ex.prerequisites.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Info className="h-4 w-4" />
                  Prerequisites
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-2">
                  {ex.prerequisites.map((prereq, i) => (
                    <div key={i} className="text-sm p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      {prereq}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}